import { Icon } from "@vds/icons";
import { DropdownSelect } from "@vds/selects";
import { Loader } from "@vds/loaders";
import { Body, Title } from "@vds/typography";
import VDSTooltip from "../../global/components/Tooltip/VDSTooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid4 } from "uuid";
import styled from "styled-components";
import EbcFloorPlan from "../../global/components/floorPlan/EbcFloorPlan";
import { getSiteNameById, truncateName } from "../../global/utility/Utils";
import brokenImage2 from "../../images/brokenImage2.jpg";
import { ImageDownloadMedia } from "../../model/mediaService";
import { fetchCountryIdsRequest, saveFloorImageMapping, fetchIncidentsByFloorRequest, fetchIncidentsByFloorSuccess, setSelectedSiteId } from "../../store/actions";
import { DASHBOARD_TEST_IDS } from "./dashboardConstants";
import "./FloorContainer.scss";
import { SiteContext } from "./index";
import { FloorMapContainer, FloorMapPlaceholder, FloorPlanWrapper, FloorTab, FloorTabText, FloorTabsContainer, TopContainerWrapper } from "./styles";
import { TEXT } from "../../global/constants/floorContainerConstants";
import "./FloorContainer.scss";
import ErrorTemplate from "../../global/components/errorTemplate/ErrorTemplate";
import { device } from "../../styles/breakpoints";

/**
 * Full-Screen Modal for Mobile Floor Plan
 * Provides immersive floor plan experience on mobile devices
 */
const FullScreenModal = styled.div`
  display: none; /* Hidden by default */

  &.active {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: #000;
    z-index: 9999;
    flex-direction: column;
  }
`;

const FullScreenHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  min-height: 56px;
`;

const FullScreenTitle = styled.div`
  font-family: Verizon NHG DS;
  font-size: 18px;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    opacity: 0.7;
  }
`;

const FullScreenContent = styled.div`
  flex: 1;
  overflow: auto;
  background: #f6f6f6;
  position: relative;
`;

const FullScreenFloorTabs = styled.div`
  display: flex;
  overflow-x: auto;
  background: rgba(0, 0, 0, 0.85);
  padding: 8px;
  gap: 8px;
  -webkit-overflow-scrolling: touch;
`;

const FullScreenFloorTab = styled.button`
  background: ${props => props.$isSelected ? '#ff8027' : 'rgba(255,255,255,0.2)'};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;

  &:active {
    background: ${props => props.$isSelected ? '#d66600' : 'rgba(255,255,255,0.3)'};
  }
`;

const FloorContainer = ({ initialSiteTag, setInitialSiteTag, initialFloorTag, setInitialFloorTag, userSelectedSiteRef }) => {
  const defaultCameraList = useSelector((state) => state.deviceReducer.cameraList);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const floorImages = useSelector((state) => state.imagesReducer.floorImages);
  const dispatch = useDispatch();
  const isMultiSite = useSelector((state) => state.sitesReducer.isMultiSite);
  const sitesTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const [siteid, setSiteid] = useState(null);
  const isLoading = useSelector((state) => state.sitesReducer.loading);
  const eventsLoading = useSelector((state) => state.eventReducer.loadingEvents);
  const siteStatsCache = useSelector((state) => state.sitesReducer.siteStatsCache);
  const siteStatsPending = useSelector((state) => state.sitesReducer.siteStatsPending);
  // Map<imageId, { floorId, status: 'pending' | 'fetched' | 'error' }>
  const imageRequestQueue = useRef(new Map());
  const isProcessingImageQueue = useRef(false);

  // const siteTagListFloorList = sitesTagList?.floors || [];

  const [orderedSites, setOrderedSites] = useState([]);
  const [floorTag, setFloorTag] = useState("");

  const { setIsSingleSite } = useContext(SiteContext);

  // Combined initialization effect to prevent race conditions
  useEffect(() => {
    if (sitesTagList?.sites?.length) {
      const allSites = sitesTagList.sites;
      let targetSite = null;
      let reordered = allSites;

      // Try to match initialSiteTag if provided
      if (initialSiteTag !== undefined) {
        targetSite = allSites.find((site) => site.id === initialSiteTag);
        if (targetSite) {
          reordered = [targetSite, ...allSites.filter((site) => site.id !== targetSite.id)];
        }
      }

      // Fallback to first site if no match
      if (!targetSite) {
        targetSite = allSites[0];
      }

      // Set all states together to prevent cascading updates
      setOrderedSites(reordered);
      setIsSingleSite(allSites.length === 1);
      if (targetSite) {
        setSiteid(targetSite.id);
        dispatch(setSelectedSiteId(targetSite.id));
        const cached = siteStatsCache?.[targetSite.id];
        const isPending = siteStatsPending?.[targetSite.id];
        if (cached) {
          dispatch(fetchIncidentsByFloorSuccess(cached));
        } else if (!isPending) {
          dispatch(fetchIncidentsByFloorRequest(targetSite.id));
        }
        if (initialFloorTag) setFloorTag(initialFloorTag);
        else {
          setFloorTag(targetSite.tagids?.[0]);
        }
      }
    }
  }, [sitesTagList, initialSiteTag, initialFloorTag]);

  const getFloor = () => {
    if (siteid) {
      let floor = sitesTagList?.floors?.filter((floor) => {
        return floor?.tagids?.includes(siteid);
      });
      return floor;
    }
    return undefined;
  };

  const getCamerasOfSite = (siteid) => {
    return defaultCameraList?.filter((cam) => {
      return cam?.tagids?.includes(siteid);
    });
  };

  const [floorid, setFloorid] = useState();
  const [floorImage, setFloorImage] = useState();
  const [cameraList, setCameraList] = useState(getCamerasOfSite(siteid));
  const [imageLoading, setImageLoading] = useState(false);
  const [siteHeader, setSiteHeader] = useState("");
  const [floorsList, setFloorList] = useState([]);

  // Mobile full-screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setWindowWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobile = windowWidth < 768;

  const handleFloorPlanClick = () => {
    if (isMobile && floorid) {
      document.body.dataset.originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      setIsFullScreen(true);
    }
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
    document.body.style.overflow = document.body.dataset.originalOverflow || '';
    delete document.body.dataset.originalOverflow;
  };

  // Close full-screen on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        closeFullScreen();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullScreen]);

  // Single effect to handle floor selection based on siteid
  useEffect(() => {
    if (siteid && floorTag) {
      setSiteHeader(getHeaderName());
      const floors = getFloor();

      if (floors?.length) {
        let targetFloor = null;

        // Try to match initialFloorTag if provided and exists in floors
        if (floorTag) {
          targetFloor = floors.find((f) => f.id === floorTag);
        }

        // Fallback to first floor if no match
        if (!targetFloor) {
          targetFloor = floors[0];
        }

        // Update all floor-related states together
        if (targetFloor) {
          const { id, imageid } = targetFloor;
          setFloorid(id);
          setFloorList(floors);
          setCameraList(getCamerasOfSite(siteid));
          if (imageid) {
            fetchFloorImage({ id, imageId: imageid });
          }
        }
      }
    }
  }, [siteid, floorTag, defaultCameraList]);

  const processImageQueue = async () => {
    if (isProcessingImageQueue.current) return;
    isProcessingImageQueue.current = true;
    try {
      const queue = imageRequestQueue.current;
      while (true) {
        const next = [...queue.entries()].find(([, v]) => v.status === "pending");
        if (!next) break;
        const [imageId, { floorId }] = next;
        try {
          setImageLoading(true);
          const url = await ImageDownloadMedia(imageId, activeSession);
          queue.get(imageId).status = "fetched";
          dispatch(saveFloorImageMapping(floorId, url));
          setFloorImage(url);
        } catch (error) {
          console.error("Error downloading image:", error);
          queue.get(imageId).status = "error";
          setFloorImage(brokenImage2);
        } finally {
          setImageLoading(false);
        }
      }
    } finally {
      isProcessingImageQueue.current = false;
    }
  };

  const fetchFloorImage = ({ id, imageId }) => {
    const matchingFloorImageTag = floorImages?.find(({ id: floorId }) => floorId === id);
    if (matchingFloorImageTag) {
      setFloorImage(matchingFloorImageTag.imageUrl);
      return;
    }
    const queue = imageRequestQueue.current;
    const existing = queue.get(imageId);
    if (existing && existing.status !== "error") return;
    queue.set(imageId, { floorId: id, status: "pending" });
    processImageQueue();
  };

  const getHeaderName = () => {
    return `${getSiteNameById(siteid, sitesTagList?.sites)}`;
  };

  const getFloorTabs = () => {
    return floorsList.map((floor = {}) => {
      return (
        <FloorTab
          key={uuid4()}
          isSelected={floorid === floor.id}
          onClick={() => {
            setFloorid(floor.id);
            fetchFloorImage({ id: floor.id, imageId: floor.imageid });
            // Manual floor selection locks auto-follow, same as manual site selection —
            // otherwise an incoming event could switch the floor back out from under the user.
            if (userSelectedSiteRef) userSelectedSiteRef.current = true;
            // Keep Dashboard's initialFloorTag in sync with the manually selected tab —
            // otherwise a later forced switch that targets the same stale value becomes
            // a no-op (React skips the state update).
            if (setInitialFloorTag) setInitialFloorTag(floor.id);
          }}
          data-testid={DASHBOARD_TEST_IDS.DASHBOARD_FLOOR_TAB}
        >
          <VDSTooltip placement="bottom" arrow title={floor.name}>
            <FloorTabText>
              <Body bold size="large">
                <span className="floor-container_floor-name">{floor.name || TEXT.UNNAMED_FLOOR}</span>
              </Body>
            </FloorTabText>
          </VDSTooltip>
          {/* <EventCountBadge>
            <EventCountBadgeText>
              3
            </EventCountBadgeText>
          </EventCountBadge> */}
        </FloorTab>
      );
    });
  };

  const getSiteDropdown = () => {
    return orderedSites?.map((site = {}, index = 0) => {
      return (
        <option key={`${site?.id}_${index}`} value={site?.id}>
          {site?.name}
        </option>
      );
    });
  };

  if (isLoading) {
    return <FloorMapPlaceholder>{isLoading && <Loader id="floor-map-loader" active={isLoading} fullscreen={false} />}</FloorMapPlaceholder>;
  }

  if (!siteid && initialSiteTag) {
    return (
      <FloorMapPlaceholder>
        <ErrorTemplate
          title={TEXT.NO_SITES_TITLE}
          description={TEXT.NO_SITES_DESC}
          onRefresh={() => dispatch(fetchCountryIdsRequest())}
          testId="dashboard-events-error"
        />
      </FloorMapPlaceholder>
    );
  }

  return (
    <>
      <TopContainerWrapper>
        {!isMultiSite && <h4 className="floor-container-multisite_header">{siteHeader}</h4>}
        {isMultiSite &&
          (orderedSites?.length ? (
            <div style={{
              width: '100%',
              maxWidth: '330px',
              position: 'relative',
              zIndex: 1
            }}>
              <DropdownSelect
                label="Site:"
                errorText="Select a Site"
                error={false}
                disabled={false}
                readOnly={false}
                inlineLabel={true}
                width="100%"
                value={siteid}
                onChange={(event) => {
                  const newSiteId = event.target.value;
                  setSiteid(newSiteId);
                  dispatch(setSelectedSiteId(newSiteId));
                  if (userSelectedSiteRef) userSelectedSiteRef.current = true;
                  // Keep Dashboard's initialSiteTag in sync with what's actually displayed —
                  // otherwise a later forced switch (e.g. scroll-to-top) that happens to target
                  // the same stale value becomes a no-op (React skips the state update).
                  if (setInitialSiteTag) setInitialSiteTag(newSiteId);
                }}
                data-testid={DASHBOARD_TEST_IDS.DASHBOARD_SITE_DROPDOWN}
              >
                {getSiteDropdown()}
              </DropdownSelect>
            </div>
          ) : (
            <></>
          ))}
        {floorid && (
          <FloorMapContainer>
            <FloorTabsContainer>{getFloorTabs()}</FloorTabsContainer>
            <FloorPlanWrapper $loading={eventsLoading}>
              <EbcFloorPlan
                floorId={floorid}
                floorImage={floorImage}
                cameras={cameraList}
                showHazard={true}
                isEditable={false}
                dashboard={true}
                imageLoading={imageLoading}
                //FloorSize={[916, 449]}
              />
            </FloorPlanWrapper>
          </FloorMapContainer>
        )}
      </TopContainerWrapper>

      {/* Full-Screen Modal for Mobile */}
      <FullScreenModal className={isFullScreen ? 'active' : ''}>
        <FullScreenHeader>
          <FullScreenTitle>
            {siteHeader} - Floor Plan
          </FullScreenTitle>
          <CloseButton onClick={closeFullScreen} aria-label="Close">
            ✕
          </CloseButton>
        </FullScreenHeader>

        {floorsList.length > 1 && (
          <FullScreenFloorTabs>
            {floorsList.map((floor = {}) => (
              <FullScreenFloorTab
                key={floor.id}
                $isSelected={floorid === floor.id}
                onClick={() => {
                  setFloorid(floor.id);
                  fetchFloorImage({ id: floor.id, imageId: floor.imageid });
                  // Manual floor selection locks auto-follow, same as manual site selection —
                  // otherwise an incoming event could switch the floor back out from under the user.
                  if (userSelectedSiteRef) userSelectedSiteRef.current = true;
                  // Keep Dashboard's initialFloorTag in sync with the manually selected tab —
                  // otherwise a later forced switch that targets the same stale value becomes
                  // a no-op (React skips the state update).
                  if (setInitialFloorTag) setInitialFloorTag(floor.id);
                }}
              >
                {floor.name || TEXT.UNNAMED_FLOOR}
              </FullScreenFloorTab>
            ))}
          </FullScreenFloorTabs>
        )}

        <FullScreenContent>
          {floorid && (
            <EbcFloorPlan
              floorId={floorid}
              floorImage={floorImage}
              cameras={cameraList}
              showHazard={true}
              isEditable={false}
              dashboard={true}
              imageLoading={imageLoading}
              fullScreen={true}
            />
          )}
        </FullScreenContent>
      </FullScreenModal>
    </>
  );
};

export default FloorContainer;